import React from 'react';
import { Modal, Button, ProgressBar, Spinner } from 'react-bootstrap';
import { ethers } from 'ethers';
import { getLzScanUrl, getExplorerTxUrl } from '../../services/bridgeService';

const STATUS_LABELS = {
  pending: 'Waiting...',
  quoting: 'Estimating fee...',
  signing: 'Confirm in wallet...',
  confirming: 'Confirming on-chain...',
  done: 'Source chain confirmed',
  error: 'Failed',
};

const LZ_STATUS_LABELS = {
  INFLIGHT: 'Cross-chain delivery in progress...',
  CONFIRMING: 'Confirming on destination...',
  DELIVERED: 'Delivered!',
  FAILED: 'Cross-chain delivery failed',
  PAYLOAD_STORED: 'Payload stored, awaiting execution...',
  BLOCKED: 'Blocked — check LayerZero Scan',
};

const StatusIcon = ({ status }) => {
  if (status === 'done') return <span style={{ color: '#b5ff16', fontSize: '1.2rem' }}>&#10003;</span>;
  if (status === 'error') return <span style={{ color: '#ff4444', fontSize: '1.2rem' }}>&#10007;</span>;
  if (status === 'pending') return <span style={{ color: '#666', fontSize: '1.2rem' }}>&#9679;</span>;
  return <Spinner animation="border" size="sm" style={{ color: '#b5ff16' }} />;
};

const BridgeModal = ({
  show,
  onClose,
  sourceChain,
  destinationChain,
  nativeCurrencySymbol,
  bridgeProgress,
  totalEstimatedFee,
  isBridging,
  bridgeComplete,
  onStartBridge,
  onRetry,
  lzStatuses = {},
}) => {
  const total = bridgeProgress.length;
  const completed = bridgeProgress.filter((p) => p.status === 'done').length;
  const failed = bridgeProgress.filter((p) => p.status === 'error').length;
  const hasStarted = bridgeProgress.some((p) => p.status !== 'pending');
  const progressPct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const sourceChainKey = sourceChain === 'Etherlink' ? 'etherlink' : 'base';

  // Check if all items have reached a fully terminal state (LZ delivered/failed, or source error)
  const allFullyComplete = bridgeComplete && bridgeProgress.every((p) => {
    if (p.status === 'error') return true; // source chain failed — terminal
    if (p.status === 'done' && p.txHash) {
      const lz = lzStatuses[p.txHash]?.status;
      return lz === 'DELIVERED' || lz === 'FAILED';
    }
    return false;
  });
  const lzDelivering = bridgeComplete && !allFullyComplete;

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" keyboard={!isBridging}>
      <Modal.Header closeButton={!isBridging}>
        <Modal.Title>
          Bridge NFTs
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Summary */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ margin: 0, fontSize: '1rem', color: '#ccc' }}>
            Bridge <strong style={{ color: '#b5ff16' }}>{total}</strong> NFT{total !== 1 ? 's' : ''} from{' '}
            <strong>{sourceChain}</strong> to <strong>{destinationChain}</strong>
          </p>
          {totalEstimatedFee && !hasStarted && (
            <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#999' }}>
              Estimated total cost: ~{parseFloat(ethers.utils.formatEther(totalEstimatedFee)).toFixed(6)}{' '}
              {nativeCurrencySymbol} (fee + gas, excess refunded)
            </p>
          )}
        </div>

        {/* Warnings (before starting) */}
        {!hasStarted && (
          <div
            style={{
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              borderLeft: '3px solid #b5ff16',
            }}
          >
            <p style={{ margin: '6px 0 0', fontSize: '0.85rem', color: '#aaa' }}>
              &#9888; Cross-chain delivery takes approximately 2-5 minutes per NFT after confirmation.
            </p>
          </div>
        )}

        {/* Progress bar */}
        {hasStarted && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '0.85rem', color: '#aaa' }}>
                {bridgeComplete
                  ? `${completed} of ${total} bridged${failed > 0 ? `, ${failed} failed` : ''}`
                  : `Bridging... ${completed} of ${total}`}
              </span>
              <span style={{ fontSize: '0.85rem', color: '#b5ff16' }}>{progressPct}%</span>
            </div>
            <ProgressBar
              now={progressPct}
              variant="success"
              style={{ height: '6px', backgroundColor: '#333' }}
            />
          </div>
        )}

        {/* NFT progress list */}
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            borderRadius: '8px',
            backgroundColor: '#111',
          }}
        >
          {bridgeProgress.map((item, idx) => (
            <div
              key={`${item.chain}-${item.tokenId}`}
              className="bridge-progress-item"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                borderBottom: idx < total - 1 ? '1px solid #222' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                <StatusIcon status={item.status} />
                <div>
                  <div style={{ fontSize: '0.95rem', color: '#fff' }}>
                    Prospect Worker #{item.tokenId}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: item.status === 'error' ? '#ff4444' : '#888' }}>
                    {item.status === 'error' ? item.error : STATUS_LABELS[item.status]}
                  </div>
                  {/* LZ cross-chain delivery status */}
                  {item.status === 'done' && item.txHash && (() => {
                    const lz = lzStatuses[item.txHash];
                    const color = lz?.status === 'DELIVERED' ? '#b5ff16'
                      : lz?.status === 'FAILED' ? '#ff4444' : '#888';
                    const label = lz
                      ? (LZ_STATUS_LABELS[lz.status] || lz.status)
                      : 'Waiting for cross-chain indexing...';
                    const isTerminal = lz?.status === 'DELIVERED' || lz?.status === 'FAILED';
                    return (
                      <div style={{ fontSize: '0.75rem', marginTop: '2px', color, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {!isTerminal && <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px', borderWidth: '1.5px', color: '#b5ff16' }} />}
                        {label}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Links / actions */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {item.txHash && (
                  <>
                    <a
                      href={getExplorerTxUrl(sourceChainKey, item.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.75rem' }}
                    >
                      Explorer
                    </a>
                    <a
                      href={getLzScanUrl(item.txHash)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.75rem' }}
                    >
                      LZ Scan
                    </a>
                  </>
                )}
                {item.status === 'error' && onRetry && (
                  <Button
                    size="sm"
                    variant="outline-warning"
                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                    onClick={() => onRetry(item.tokenId)}
                    disabled={isBridging}
                  >
                    Retry
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'center' }}>
        {!hasStarted && (
          <Button variant="success" onClick={onStartBridge} style={{ minWidth: '180px' }}>
            Start Bridge
          </Button>
        )}
        {hasStarted && !bridgeComplete && (
          <Button variant="secondary" disabled>
            <Spinner animation="border" size="sm" className="me-2" />
            Bridging...
          </Button>
        )}
        {lzDelivering && (
          <Button variant="secondary" disabled>
            <Spinner animation="border" size="sm" className="me-2" />
            Delivering to {destinationChain}...
          </Button>
        )}
        {allFullyComplete && (
          <Button variant="success" onClick={onClose} style={{ minWidth: '180px' }}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BridgeModal;
